import React, { useEffect } from "react";
import { useTranslate, useUpdate } from "@refinedev/core";
import { Form, Modal, Button, message, Typography, theme } from "antd";
import { ITenant } from "../../models/tenant";
import { TENANT_SETTINGS_KEY } from "../../authProvider";
import { TenantApiKeyFields } from "./TenantGeneralFields";

const { Text } = Typography;

type CredentialValues = {
  bFlashApiKey?: string;
  autotunerMastId?: string;
  autotunerApiKey?: string;
};

type TenantApiCredentialsModalProps = {
  open: boolean;
  initialValues?: CredentialValues;
  onCancel: () => void;
  onSuccess?: () => void;
};

const toFieldString = (value: unknown): string | undefined => {
  if (value == null || value === "") {
    return undefined;
  }
  if (typeof value === "string") {
    return value;
  }
  return String(value);
};

/**
 * Modal to edit and immediately persist tenant API credentials (BFlash / Autotuner).
 */
export const TenantApiCredentialsModal: React.FC<
  TenantApiCredentialsModalProps
> = ({ open, initialValues, onCancel, onSuccess }) => {
  const _t = useTranslate();
  const { token } = theme.useToken();
  const [form] = Form.useForm<CredentialValues>();
  const { mutate: update, isLoading: isUpdating } = useUpdate();
  const s = (key: string) => _t(`settings.generalSettingsCard.${key}`);

  useEffect(() => {
    if (!open) {
      form.resetFields();
      return;
    }
    form.setFieldsValue({
      bFlashApiKey: toFieldString(initialValues?.bFlashApiKey),
      autotunerMastId: toFieldString(initialValues?.autotunerMastId),
      autotunerApiKey: toFieldString(initialValues?.autotunerApiKey),
    });
  }, [open, initialValues, form]);

  const handleFinish = (values: CredentialValues) => {
    update(
      {
        resource: "tenants",
        id: "my-tenant",
        values: {
          bFlashApiKey: values.bFlashApiKey,
          autotunerMastId: values.autotunerMastId,
          autotunerApiKey: values.autotunerApiKey,
        },
      },
      {
        onSuccess: (data) => {
          message.success(_t("settings.generalSettingsSaveSuccess"));
          const updated = data?.data as ITenant | undefined;
          if (updated?.settings) {
            localStorage.setItem(
              TENANT_SETTINGS_KEY,
              JSON.stringify(updated.settings),
            );
            window.dispatchEvent(new Event("tenantSettingsUpdated"));
          }
          onSuccess?.();
          onCancel();
        },
        onError: () => {
          message.error(_t("settings.generalSettingsSaveError"));
        },
      },
    );
  };

  return (
    <Modal
      open={open}
      title={s("apiKeysTitle")}
      onCancel={onCancel}
      destroyOnClose
      width={640}
      footer={[
        <Button key="cancel" onClick={onCancel} disabled={isUpdating}>
          {_t("buttons.cancel")}
        </Button>,
        <Button
          key="save"
          type="primary"
          loading={isUpdating}
          onClick={() => form.submit()}
        >
          {_t("buttons.save")}
        </Button>,
      ]}
    >
      <Text
        className="text-sm block mb-6"
        style={{ color: token.colorTextSecondary }}
      >
        {s("apiKeysSubtitle")}
      </Text>
      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        className="general-settings-form"
      >
        <TenantApiKeyFields variant="rich" hideHeader />
      </Form>
    </Modal>
  );
};
